import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const BannerRow = ({ items, testid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 md:gap-3 items-center"
    >
      {items.map((b, i) => (
        <a
          key={i}
          href={b.url || "#"}
          target="_blank"
          rel="nofollow sponsored noopener noreferrer"
          data-testid={`${testid}-banner-${i}`}
          className="block w-full border border-white/10 hover:border-[var(--hk-red)] transition-colors bg-black"
        >
          <img
            src={b.image}
            alt={b.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="block w-full h-auto object-contain"
          />
        </a>
      ))}
    </div>
  );
};

const BannerDisclaimer = ({ text, testid }) => (
  <div
    data-testid={testid}
    className="max-w-4xl mx-auto border border-white/10 bg-white/[0.03] px-4 py-3 text-center text-[10px] md:text-[11px] leading-relaxed text-white/40"
  >
    {text}
  </div>
);

const FreeTip = () => {
  const { t } = useLang();
  const [tip, setTip] = useState(null);
  const [banners, setBanners] = useState({ top: [], bottom: [] });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/freetip`);
        if (!ignore) setTip(data);
      } catch (e) {
        console.error("free tip fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/settings/banners`);
        if (!ignore) setBanners({ top: data.top || [], bottom: data.bottom || [] });
      } catch (e) {
        console.error("banners fetch failed", e);
      }
      try {
        const { data } = await axios.get(`${API}/freetips/history`);
        if (!ignore) setHistory(data);
      } catch (e) {
        console.error("free tip history fetch failed", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <section id="free-tip" data-testid="free-tip-section" className="py-16 md:py-24">
      <div className="container-hk space-y-10 md:space-y-14">
        {banners.top.length > 0 && (
          <BannerDisclaimer text={t.freeTip.banner_disclaimer} testid="banners-disclaimer-top" />
        )}
        <BannerRow items={banners.top} testid="banners-top" />

        <div className="text-center">
          <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
            // {t.freeTip.overline}
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl uppercase leading-none tracking-tight">
            {t.freeTip.title}
          </h2>
        </div>

        {tip && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="free-tip-card"
            className="relative max-w-3xl mx-auto border border-[var(--hk-red)]/40 bg-[var(--hk-surface)] p-6 md:p-10 overflow-hidden"
          >
            <div className="absolute top-0 left-0 bg-[var(--hk-red)] text-white text-[9px] font-bold tracking-[0.3em] px-3 py-1.5">
              {t.freeTip.free_badge}
            </div>
            <div data-testid="free-tip-date" className="text-center font-mono text-xs text-white/50 tracking-[0.2em] mb-8">
              {tip.date}
            </div>
            <div className="flex items-center justify-center gap-4 md:gap-8">
              <div data-testid="free-tip-home" className="flex-1 font-display text-xl md:text-3xl uppercase tracking-tight text-right">
                {tip.home}
              </div>
              <div className="text-[var(--hk-red)] font-display text-sm md:text-lg tracking-[0.2em] shrink-0">VS</div>
              <div data-testid="free-tip-away" className="flex-1 font-display text-xl md:text-3xl uppercase tracking-tight text-left">
                {tip.away}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-8">
              <div className="bg-black p-4 md:p-6 text-center">
                <div className="text-[9px] tracking-[0.3em] text-white/50 mb-2">{t.freeTip.tip}</div>
                <div data-testid="free-tip-pick" className="font-display text-xl md:text-2xl text-[var(--hk-red)]">{tip.pick}</div>
              </div>
              <div className="bg-black p-4 md:p-6 text-center">
                <div className="text-[9px] tracking-[0.3em] text-white/50 mb-2">{t.freeTip.odds}</div>
                <div data-testid="free-tip-odds" className="font-display text-xl md:text-2xl">{tip.odds}</div>
              </div>
            </div>
          </motion.div>
        )}

        <div
          data-testid="free-tip-note"
          className="max-w-3xl mx-auto border border-white/10 bg-white/[0.03] px-4 py-3 text-center text-[10px] md:text-[11px] leading-relaxed text-white/40"
        >
          {t.freeTip.note}
        </div>

        <BannerRow items={banners.bottom} testid="banners-bottom" />
        {banners.bottom.length > 0 && (
          <BannerDisclaimer text={t.freeTip.banner_disclaimer} testid="banners-disclaimer-bottom" />
        )}

        {history.length > 0 && (
          <div data-testid="freetip-history" className="max-w-3xl mx-auto w-full">
            <div className="text-[10px] tracking-[0.35em] text-white/50 mb-4 text-center">
              // {t.freeTip.history}
            </div>
            <div className="border border-white/10">
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 md:gap-8 px-4 md:px-6 py-2.5 bg-white/5 text-[9px] tracking-[0.3em] text-white/40">
                <span>{t.freeTip.match}</span>
                <span>{t.freeTip.tip}</span>
                <span>{t.freeTip.odds}</span>
                <span className="text-right">{t.freeTip.result}</span>
              </div>
              <div className="divide-y divide-white/10">
                {history.map((h, i) => (
                  <div
                    key={h.id}
                    data-testid={`freetip-history-row-${i}`}
                    className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 md:gap-8 px-4 md:px-6 py-3.5 bg-black"
                  >
                    <span data-testid={`freetip-history-match-${i}`} className="text-sm truncate">{h.match}</span>
                    <span className="text-xs text-white/60 whitespace-nowrap">{h.tip}</span>
                    <span className="font-mono text-xs text-white/70 whitespace-nowrap">{h.odds}</span>
                    <span
                      className={`justify-self-end px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] ${
                        h.result === "WIN"
                          ? "bg-[#10B981]/15 text-[#10B981]"
                          : h.result === "LOSS"
                            ? "bg-[var(--hk-red)]/15 text-[var(--hk-red)]"
                            : "bg-white/10 text-white/60"
                      }`}
                    >
                      {h.result}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => scrollToId("plans")}
            data-testid="freetip-btn-plans"
            className="btn-red px-10 py-4 text-[11px] font-bold tracking-[0.3em] w-full sm:w-auto"
          >
            {t.freeTip.btn_plans} →
          </button>
          <button
            onClick={() => scrollToId("archive")}
            data-testid="freetip-btn-archive"
            className="btn-outline px-10 py-4 text-[11px] font-bold tracking-[0.3em] w-full sm:w-auto"
          >
            {t.freeTip.btn_archive} →
          </button>
        </div>
      </div>
    </section>
  );
};

export default FreeTip;
